import { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  TextField,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Grid2,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';

import { AddCircle, Edit, SearchNormal1, Bag, Eye } from 'iconsax-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';

import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import CustomTable from 'components/@extended/CustomTable';
import SkeletonTable from 'components/SkeletonTable';
import IconButton from 'components/@extended/IconButton';

import { Column } from 'react-table';
import {
  getAllQualificationPacks,
  getQualificationPackById,
  deleteQualificationPack,
  updateQualificationPack
} from '../../services/qualificationPack.service';

const QualificationPackList = () => {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [size, setSize] = useState(10);
  const [sortArray, setSortArray] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Delete dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedQP, setSelectedQP] = useState<any>(null);

  // View detail modal
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedForView, setSelectedForView] = useState<any>(null);
  const [viewDetailLoading, setViewDetailLoading] = useState(false);
  const fetchedViewIdRef = useRef<string | null>(null);

  // Fetch Qualification Packs
  const fetchQualificationPacks = async () => {
    if (!organization?._id) return;

    try {
      setIsLoading(true);
      const reqParams = {
        deleted: false,
        skip: pageIndex,
        limit: size,
        search,
        orgId: organization?._id
      };

      const res = await getAllQualificationPacks(reqParams);

      if (res?.status === 200) {
        setData(res?.data?.data?.qualificationPacks || res?.data?.data || []);
        setCount(res?.data?.data?.count || res?.data?.total || 0);
      }
    } catch (err) {
      console.error('Fetch qualification packs failed:', err);
      Snackbar('Failed to load qualification packs', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (organization?._id) {
      fetchQualificationPacks();
    }
  }, [organization?._id, pageIndex, size, search, sortArray]);

  // Fetch full QP (with NOS + PC) when view modal opens
  useEffect(() => {
    if (!openViewModal || !selectedForView?._id) return;
    if (fetchedViewIdRef.current === selectedForView._id) return;
    fetchedViewIdRef.current = selectedForView._id;
    setViewDetailLoading(true);
    getQualificationPackById(selectedForView._id)
      .then((res) => {
        const d = res?.data?.data;
        if (d) setSelectedForView(d);
      })
      .catch(() => {
        Snackbar('Failed to load qualification pack details', 'error');
      })
      .finally(() => setViewDetailLoading(false));
  }, [openViewModal, selectedForView?._id]);

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    fetchedViewIdRef.current = null;
  };

  // Sorting handler
  const handleSortChange = (column: any) => {
    const id = column.name || column.id || column.accessor;
    const index = sortArray.findIndex((item) => item.id === id);

    const next = [...sortArray];

    if (index === -1) {
      next.push({ id, sort: true, desc: false });
    } else if (!next[index].desc) {
      next[index].desc = true;
    } else {
      next.splice(index, 1);
    }

    setSortArray(next);
  };

  // Delete handlers
  const handleConfirmDelete = async () => {
    if (!selectedQP?._id) {
      Snackbar('No qualification pack selected', 'error');
      return;
    }

    try {
      await deleteQualificationPack(selectedQP._id);
      Snackbar('Qualification Pack deleted successfully', 'success');

      setOpenDeleteDialog(false);
      setSelectedQP(null);
      fetchQualificationPacks();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete qualification pack';
      Snackbar(msg, 'error');
      console.error('Delete QP error:', err);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedQP(null);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: 'QP Code',
        accessor: 'qpCode'
      },
      {
        Header: 'QP Name',
        accessor: 'qpName'
      },
      {
        Header: 'Sector Skill Council',
        accessor: (row: any) => row?.sscId?.name || row?.sscName || '—'
      },
      {
        Header: 'NSQF Level',
        accessor: 'nsqfLevel'
      },
      {
        Header: 'Version',
        accessor: (row: any) => row?.version || '—'
      },
      {
        Header: 'Created By',
        accessor: (row: any) => {
          const createdBy = row?.createdBy;
          if (!createdBy) return '—';

          const fullName = `${createdBy?.firstName ?? ''} ${createdBy?.lastName ?? ''}`.trim();
          const title = createdBy?.email ?? fullName ?? '—';

          return (
            <Tooltip title={title}>
              <span>{fullName || createdBy?.email || '—'}</span>
            </Tooltip>
          );
        }
      },
      {
        Header: 'Locked',
        accessor: 'isLocked',
        Cell: ({ value }: any) => (
          <Typography
            variant="caption"
            sx={{
              px: 1.2,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 600,
              color: value ? 'warning.main' : 'text.secondary',
              backgroundColor: value ? 'warning.lighter' : 'grey.100',
              display: 'inline-block'
            }}
          >
            {value ? 'Locked' : '—'}
          </Typography>
        )
      },
      {
        Header: 'Total Marks',
        accessor: 'totalMarks'
      },
      {
        Header: 'Pass %',
        accessor: 'passPercentage',
        Cell: ({ value }: any) => (value != null ? `${value}%` : '—')
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ row }: any) => {
          const isActive = row.original.status === 'Active';
          const isAssignedToCourse = row.original.isAssignedToCourse === true;

          return (
            <Switch
              checked={isActive}
              color="primary"
              disabled={isAssignedToCourse}
              onChange={async (e) => {
                if (isAssignedToCourse) return;
                const newStatus = e.target.checked ? 'Active' : 'Inactive';

                try {
                  await updateQualificationPack(row.original._id, {
                    status: newStatus
                  });

                  Snackbar('Status updated successfully', 'success');

                  setData((prev) => prev.map((item) => (item._id === row.original._id ? { ...item, status: newStatus } : item)));
                } catch (err: any) {
                  Snackbar(err?.response?.data?.message || 'Failed to update status', 'error');
                }
              }}
            />
          );
        }
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const isAssignedToCourse = row.original.isAssignedToCourse === true;
          const isLockedForDelete = row.original.isLocked === true || isAssignedToCourse;
          const editTooltip = isAssignedToCourse ? 'Assigned to course – cannot edit or unlock' : 'Edit';
          const deleteTooltip = isAssignedToCourse
            ? 'Assigned to course – cannot delete'
            : row.original.isLocked
            ? 'Locked – cannot delete'
            : 'Delete';
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="View">
                <IconButton
                  color="secondary"
                  onClick={() => {
                    setSelectedForView(row.original);
                    setOpenViewModal(true);
                  }}
                >
                  <Eye />
                </IconButton>
              </Tooltip>

              <Tooltip title={editTooltip}>
                <span>
                  <IconButton
                    color="primary"
                    disabled={isAssignedToCourse}
                    onClick={() =>
                      navigate('/qualificationpack/edit', {
                        state: { isEdit: true, data: row.original }
                      })
                    }
                  >
                    <Edit />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={deleteTooltip}>
                <span>
                  <IconButton
                    color="error"
                    disabled={isLockedForDelete}
                    onClick={() => {
                      setSelectedQP(row.original);
                      setOpenDeleteDialog(true);
                    }}
                  >
                    <Bag />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        }
      }
    ],
    [navigate]
  ) as Column<any>[];

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1.5}>
        <MainCard
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <BackToggle show />
              <Box>
                <Typography variant="h5">
                  <FormattedMessage id="Qualification Pack List" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage qualification packs and their details
                </Typography>
              </Box>
            </Stack>
          }
          content={false}
          secondary={
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search QPs..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPageIndex(1);
                    setSearch(searchInput);
                  }
                }}
              />

              <IconButton
                variant="outlined"
                onClick={() => {
                  setPageIndex(1);
                  setSearch(searchInput);
                }}
                disabled={!searchInput}
              >
                <SearchNormal1 />
              </IconButton>

              <IconButton variant="dashed" onClick={() => navigate('/qualificationpack/add')}>
                <AddCircle />
              </IconButton>
            </Stack>
          }
        />

        <MainCard content={false}>
          {isLoading ? (
            <SkeletonTable columns={columns} />
          ) : (
            <CustomTable
              columns={columns}
              data={data}
              count={count}
              size={size}
              pageIndex={pageIndex}
              sortArray={columns.map((c: any) => c.accessor || c.id).filter(Boolean)}
              sort={sortArray}
              setSortingData={handleSortChange}
              handlePageChange={(event: any, page: number) => setPageIndex(page)}
              handleSizeChange={(e: SelectChangeEvent<number>) => {
                setSize(Number(e.target.value));
                setPageIndex(1);
              }}
            />
          )}
        </MainCard>
      </Box>

      {/* View Detail Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <FormattedMessage id="Qualification Pack Details" />
        </DialogTitle>
        <DialogContent dividers>
          {viewDetailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            selectedForView && (
              <Grid2 container spacing={2.5}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Sector Skill Council
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedForView?.sscId?.name ?? selectedForView?.sscName ?? '—'}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    QP Code
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedForView?.qpCode ?? '—'}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    QP Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedForView?.qpName ?? '—'}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Version
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedForView?.version ?? '—'}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    NSQF Level
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedForView?.nsqfLevel ?? '—'}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Pass Percentage
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedForView?.passPercentage != null ? `${selectedForView.passPercentage}%` : '—'}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Marks
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedForView?.totalMarks ?? '—'}
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      size="small"
                      label={selectedForView?.status ?? '—'}
                      color={selectedForView?.status === 'Active' ? 'success' : 'default'}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Locked
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {selectedForView?.isLocked ? (
                      <Chip size="small" label="Locked" color="warning" />
                    ) : (
                      <Typography variant="body1" fontWeight={500}>
                        Unlocked
                      </Typography>
                    )}
                  </Box>
                </Grid2>
                <Grid2 size={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                    NOS with Performance Criteria (PC)
                  </Typography>
                  {(() => {
                    const nosList = selectedForView?.nosIds;
                    if (!Array.isArray(nosList) || nosList.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      );
                    }
                    return (
                      <Stack spacing={2}>
                        {nosList.map((nos: any) => {
                          const nosId = nos?._id ?? nos;
                          const nosLabel = typeof nos === 'string' ? nos : `${nos?.nosCode ?? ''} - ${nos?.nosName ?? ''}`.trim() || '—';
                          const pcList = Array.isArray(nos?.pcIds) ? nos.pcIds : [];
                          return (
                            <Box
                              key={nosId}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1.5,
                                backgroundColor: 'grey.50'
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                {nosLabel}
                              </Typography>
                              {pcList.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                                  No PC
                                </Typography>
                              ) : (
                                <Stack spacing={0.75} sx={{ pl: 1 }}>
                                  {pcList.map((pc: any) => (
                                    <Box
                                      key={pc?._id ?? pc}
                                      sx={{
                                        py: 0.75,
                                        px: 1,
                                        borderRadius: 0.5,
                                        backgroundColor: 'background.paper',
                                        borderLeft: '3px solid',
                                        borderLeftColor: pc?.isCritical ? 'warning.main' : 'primary.main'
                                      }}
                                    >
                                      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
                                        <Typography variant="body2" fontWeight={600}>
                                          {pc?.pcCode ?? '—'}
                                        </Typography>
                                        {pc?.isCritical && <Chip size="small" label="Critical" color="warning" />}
                                        <Typography variant="body2" color="text.secondary">
                                          ({pc?.totalMarks ?? 0} marks{pc?.minMarks != null ? `, min ${pc.minMarks}` : ''})
                                        </Typography>
                                      </Stack>
                                      <Typography variant="body2">{pc?.pcName ?? '—'}</Typography>
                                      {pc?.description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                          {pc.description}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Stack>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    );
                  })()}
                </Grid2>
              </Grid2>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewModal}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              handleCloseViewModal();
              navigate('/qualificationpack/edit', { state: { isEdit: true, data: selectedForView } });
            }}
            disabled={selectedForView?.isAssignedToCourse === true}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Delete Qualification Pack</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedQP?.qpName || selectedQP?.qpCode || 'this pack'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={selectedQP?.isAssignedToCourse === true}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QualificationPackList;
